/**
 * The grade a number of score points earns — CR-6269.
 *
 * The OTIF and PPM Score Mappings cards list the points each percentage or
 * PPM range contributes to the rating formula. Next to those points the OTIF
 * card printed a fixed Excellent / Good / Fair / Poor badge keyed off the
 * row's OTIF %, and the PPM card once did the same, so a customer whose Grade
 * Thresholds tab said "Platinum" for 90–100 read "Excellent" on the very next
 * tab. This badge grades the row's points on the Supplier Grade Thresholds —
 * `getGradeInfo`, the rule every rating badge shares — and paints it in the
 * band's palette colour, with the band's description as the tooltip.
 */
import { SupplierRatingThreshold } from '../../../lib/api';
import { sortRatingBands } from '../../../hooks/useRatingBands';
import { getGradeInfo, ratingBadgeStyle } from '../../../pages/supplier-performance/utils';

interface ScoreGradeBadgeProps {
  /** Score points the mapping row awards. */
  score: string | number;
  /** The company's Supplier Grade Thresholds, in any order. */
  bands: SupplierRatingThreshold[] | undefined;
}

export function ScoreGradeBadge({ score, bands }: ScoreGradeBadgeProps) {
  const ordered = sortRatingBands(bands ?? []);
  const points = typeof score === 'number' ? score : parseFloat(score);
  const grade = getGradeInfo(Number.isFinite(points) ? points : 0, ordered);
  const band = ordered.find((b) => b.grade === grade.grade);
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
      style={ratingBadgeStyle(grade.color)}
      title={band?.description || undefined}
      data-grade={grade.grade}
    >
      {grade.label}
    </span>
  );
}
